import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import {
  getAssistantStatistics,
  listAssistantFolders,
  createAssistantFolder,
  renameAssistantFolder,
  deleteAssistantFolder,
  moveAssistantConversation,
  getAssistantUsage,
  createAssistantConversation,
  createAssistantPromptTemplate,
  deleteAssistantPromptTemplate,
  getAssistantPromptTemplate,
  updateAssistantPromptTemplate,
  deleteAssistantConversation,
  getAssistantConversation,
  listAssistantConversations,
  listAssistantPromptTemplates,
  renameAssistantConversation,
} from "./assistant.api"
import type { AssistantFolderFilter } from "./assistant.types"
import {
  finishAssistantRequest,
  pendingAssistantDispatches,
} from "./assistant.pending-stop"
import { clearAssistantConversationCompletionState } from "./assistant.completion-notifications"
import { clearCompletionInboxResource } from "@/entities/notification/completion-inbox"
import { closeAssistantConversationNotification } from "./assistant.completion-monitor"

/**
 * A key factory rather than a bare string enum. The flat enum values elsewhere in the app already
 * collide across entities — `AccountQueryKeysEnum.FetchUserInfo` and
 * `BackOfficeQueryKeysEnum.FetchUserInfo` are both `"fetchUserInfo"`, so invalidating one
 * invalidates the other. Namespacing under a literal prefix makes that impossible here.
 */
export const assistantKeys = {
  statistics: (filters: import("./assistant.statistics").StatisticsFilters) =>
    ["assistant", "statistics", filters] as const,
  usage: () => ["assistant", "usage"] as const,
  access: () => ["assistant", "access"] as const,
  promptTemplates: () => ["assistant", "prompt-templates"] as const,
  promptTemplate: (templateId: string) =>
    ["assistant", "prompt-templates", templateId] as const,
  all: ["assistant"] as const,
  conversations: () => ["assistant", "conversations"] as const,
  folders: () => ["assistant", "folders"] as const,
  conversationPages: (folderId: AssistantFolderFilter) =>
    [
      "assistant",
      "conversations",
      "list",
      "pages",
      folderId === undefined ? "all" : folderId,
    ] as const,
  conversationList: (pageSize?: number, offset?: number) =>
    [
      "assistant",
      "conversations",
      "list",
      pageSize ?? null,
      offset ?? null,
    ] as const,
  conversation: (conversationId: string) =>
    ["assistant", "conversations", conversationId] as const,
}

export const useAssistantConversationsQuery = ({
  pageSize,
  offset,
  enabled = true,
}: {
  pageSize?: number
  offset?: number
  enabled?: boolean
}) =>
  useQuery({
    queryKey: assistantKeys.conversationList(pageSize, offset),
    queryFn: () => listAssistantConversations({ pageSize, offset }),
    enabled,
  })

export const useAssistantConversationQuery = ({
  conversationId,
  enabled = true,
}: {
  conversationId: string
  enabled?: boolean
}) => {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: assistantKeys.conversation(conversationId),
    queryFn: async () => {
      const previous = queryClient.getQueryData<
        Awaited<ReturnType<typeof getAssistantConversation>>
      >(assistantKeys.conversation(conversationId))
      const transcript = await getAssistantConversation({ conversationId })
      transcript.finishedRequestIds?.forEach(finishAssistantRequest)
      if (
        previous &&
        ((previous.activeRun &&
          previous.activeRun.id !== transcript.activeRun?.id) ||
          previous.conversation.messageCount !==
            transcript.conversation.messageCount)
      ) {
        void queryClient.invalidateQueries({
          queryKey: [...assistantKeys.conversations(), "list"],
        })
      }
      return transcript
    },
    enabled: enabled && Boolean(conversationId),
    refetchInterval: (query) =>
      query.state.data?.activeRun ||
      pendingAssistantDispatches(conversationId).length
        ? 1000
        : false,
  })
  return {
    ...query,
    isAwaitingAcceptance:
      !query.data?.activeRun &&
      pendingAssistantDispatches(conversationId).length > 0,
  }
}

/**
 * Every mutation here reorders or removes something in the list, so they all invalidate the whole
 * conversations subtree. Renaming also has to drop the single-conversation entry, which carries
 * its own copy of the title.
 */
const useInvalidateAssistant = () => {
  const queryClient = useQueryClient()
  return {
    conversations: () =>
      queryClient.invalidateQueries({
        queryKey: assistantKeys.conversations(),
      }),
  }
}

export const useCreateAssistantConversationMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (
    conversation: Awaited<ReturnType<typeof createAssistantConversation>>
  ) => void
  onError?: (error: unknown) => void
} = {}) => {
  const invalidate = useInvalidateAssistant()
  return useMutation({
    mutationFn: createAssistantConversation,
    onSuccess: async (conversation) => {
      await invalidate.conversations()
      if (onSuccess) onSuccess(conversation)
    },
    onError,
  })
}

export const useRenameAssistantConversationMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void
  onError?: (error: unknown) => void
} = {}) => {
  const invalidate = useInvalidateAssistant()
  return useMutation({
    mutationFn: renameAssistantConversation,
    onSuccess: async () => {
      await invalidate.conversations()
      if (onSuccess) onSuccess()
    },
    onError,
  })
}

export const useDeleteAssistantConversationMutation = ({
  onSuccess,
  onError,
}: {
  /** Receives the variables this completion belongs to, so a caller can tell two deletes apart. */
  onSuccess?: (deleted: { conversationId: string }) => void
  onError?: (error: unknown) => void
} = {}) => {
  const invalidate = useInvalidateAssistant()
  return useMutation({
    mutationFn: deleteAssistantConversation,
    onSuccess: async (_data, variables) => {
      clearAssistantConversationCompletionState(variables.conversationId)
      clearCompletionInboxResource("assistant", variables.conversationId)
      closeAssistantConversationNotification(variables.conversationId)
      await invalidate.conversations()
      if (onSuccess) onSuccess(variables)
    },
    onError,
  })
}

/**
 * The shared rubric library.
 *
 * Long-lived in cache: a rubric changes when standards change, not between page views, and the
 * bodies run to tens of thousands of characters.
 */
export const useAssistantPromptTemplates = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: assistantKeys.promptTemplates(),
    queryFn: listAssistantPromptTemplates,
    staleTime: 5 * 60 * 1000,
    // Fetched on demand. The response is names rather than rubrics now, but it is still the whole
    // library, and a tab nobody opened should not fetch one.
    enabled,
  })

export const useAssistantPromptTemplateQuery = ({
  templateId,
  enabled = true,
}: {
  templateId: string
  enabled?: boolean
}) =>
  useQuery({
    queryKey: assistantKeys.promptTemplate(templateId),
    queryFn: () => getAssistantPromptTemplate(templateId),
    enabled,
  })

/**
 * Invalidates the whole `prompt-templates` subtree.
 *
 * The list and each rubric's own entry are siblings under it, and a write can change both — a
 * rename shows in the list, an edit in the editor. `promptTemplate(id)` would leave the list
 * stale, and the chat window's picker reads that list.
 */
const invalidateTemplates = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: assistantKeys.promptTemplates() })

export const useCreateAssistantPromptTemplateMutation = (options?: {
  onSuccess?: (
    template: Awaited<ReturnType<typeof createAssistantPromptTemplate>>
  ) => void
  onError?: (error: unknown) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createAssistantPromptTemplate,
    onSuccess: async (template) => {
      await invalidateTemplates(queryClient)
      options?.onSuccess?.(template)
    },
    onError: options?.onError,
  })
}

export const useUpdateAssistantPromptTemplateMutation = (options?: {
  onSuccess?: (
    template: Awaited<ReturnType<typeof updateAssistantPromptTemplate>>
  ) => void
  onError?: (error: unknown) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateAssistantPromptTemplate,
    onSuccess: async (template) => {
      await invalidateTemplates(queryClient)
      options?.onSuccess?.(template)
    },
    onError: options?.onError,
  })
}

export const useDeleteAssistantPromptTemplateMutation = (options?: {
  onSuccess?: () => void
  onError?: (error: unknown) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteAssistantPromptTemplate,
    onSuccess: async () => {
      await invalidateTemplates(queryClient)
      options?.onSuccess?.()
    },
    onError: options?.onError,
  })
}

/**
 * What the assistant has spent lately.
 *
 * Refetches on every mount rather than trusting a `staleTime`. This page is opened deliberately to
 * read a number, and showing a total that predates the call someone just made is the one thing it
 * must not do — a cached figure is indistinguishable from a current one.
 *
 * Invalidating from the send path was the alternative and is not enough: an evaluation is started
 * from the review page and never goes through chat, so that hook would miss exactly the calls this
 * report exists to show. The response is one row per kind, so refetching is cheap.
 */
export const useAssistantUsageQuery = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: assistantKeys.usage(),
    queryFn: getAssistantUsage,
    enabled,
    refetchOnMount: "always",
    staleTime: 0,
  })

export const useAssistantStatisticsQuery = (
  filters: import("./assistant.statistics").StatisticsFilters,
  enabled: boolean
) =>
  useQuery({
    queryKey: assistantKeys.statistics(filters),
    queryFn: () => getAssistantStatistics(filters),
    enabled,
    refetchOnMount: "always",
    staleTime: 0,
  })

const SIDEBAR_PAGE_SIZE = 50

/** Filter on the server before paging, so an older folder is never hidden by recent chats. */
export const useAssistantConversationPages = (
  folderId: AssistantFolderFilter
) =>
  useInfiniteQuery({
    queryKey: assistantKeys.conversationPages(folderId),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      listAssistantConversations({
        pageSize: SIDEBAR_PAGE_SIZE,
        offset: pageParam,
        folderId,
      }),
    getNextPageParam: (lastPage, _pages, lastOffset) =>
      lastPage.length === SIDEBAR_PAGE_SIZE
        ? lastOffset + SIDEBAR_PAGE_SIZE
        : undefined,
  })

export const useAssistantFolders = () =>
  useQuery({
    queryKey: assistantKeys.folders(),
    queryFn: listAssistantFolders,
  })

const useInvalidateOrganization = () => {
  const client = useQueryClient()
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: assistantKeys.folders() }),
      client.invalidateQueries({ queryKey: assistantKeys.conversations() }),
    ])
}

export const useCreateAssistantFolder = () => {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: createAssistantFolder,
    onSuccess: invalidate,
  })
}
export const useRenameAssistantFolder = () => {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: renameAssistantFolder,
    onSuccess: invalidate,
  })
}
export const useDeleteAssistantFolder = () => {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: deleteAssistantFolder,
    onSuccess: invalidate,
  })
}
export const useMoveAssistantConversation = () => {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: moveAssistantConversation,
    onSuccess: invalidate,
  })
}
